import React, { useState, useEffect, useCallback, useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box, Typography, Grid, Paper, Button, TextField, Alert, Snackbar,
  Select, MenuItem, FormControl, InputLabel, Switch, FormControlLabel,
  CircularProgress, Chip, Divider
} from '@mui/material';
import {
  Email as EmailIcon,
  Save as SaveIcon,
  Sms as SmsIcon,
  Payment as PaymentIcon,
  CheckCircle as CheckCircleIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import { fetchJson } from '../../../utils/fetchJson';
import { usePlatform } from '../../../contexts/PlatformContext';
import { UserContext } from '../../../contexts/UserContext';

export default function IntegrationsPage() {
  const { t } = useTranslation();
  const { platformName } = usePlatform();
  const user = useContext(UserContext);
  const [cfg, setCfg] = useState({ email: {}, sms: {}, mtnMomo: {}, orangeMoney: {}, stripe: {} });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testingEmail, setTestingEmail] = useState(false);
  const [testingSms, setTestingSms] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const show = (message, severity = 'success') => setSnackbar({ open: true, message, severity });

  const canManage = useMemo(() => {
    if (user?.role === 'SystemAdmin') return true;
    const perms = user && Array.isArray(user.systemPermissions) ? user.systemPermissions : null;
    if (!perms) return true;
    if (perms.includes('*')) return true;
    return perms.includes('integrations');
  }, [user]);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const data = await fetchJson('/api/global-settings/integrations');
      if (data?.integrations) {
        const mtn = data.integrations.mtnMomo || {};
        const orange = data.integrations.orangeMoney || {};
        setCfg({
          email: data.integrations.email || {},
          sms: data.integrations.sms || {},
          mtnMomo: {
            ...mtn,
            apiUserId: mtn.apiUserId || mtn.apiUser || '',
            environment: mtn.environment || mtn.targetEnvironment || 'sandbox',
            webhookUrl: mtn.webhookUrl || '',
          },
          orangeMoney: {
            ...orange,
            merchantKey: orange.merchantKey || orange.merchantCode || orange.apiKey || '',
            authHeader: orange.authHeader || '',
            baseURL: orange.baseURL || 'https://api.orange.com',
            country: orange.country || 'cm',
            returnUrl: orange.returnUrl || '',
            cancelUrl: orange.cancelUrl || '',
            notifUrl: orange.notifUrl || '',
          },
          stripe: data.integrations.stripe || {},
        });
      }
    } catch (e) {
      show(e?.message || 'Failed to load integrations', 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        ...cfg,
        mtnMomo: {
          ...cfg.mtnMomo,
          apiUser: cfg.mtnMomo?.apiUser || cfg.mtnMomo?.apiUserId || '',
          apiUserId: cfg.mtnMomo?.apiUserId || cfg.mtnMomo?.apiUser || '',
          targetEnvironment: cfg.mtnMomo?.targetEnvironment || cfg.mtnMomo?.environment || 'sandbox',
          environment: cfg.mtnMomo?.environment || cfg.mtnMomo?.targetEnvironment || 'sandbox',
        },
        orangeMoney: {
          ...cfg.orangeMoney,
          merchantKey: cfg.orangeMoney?.merchantKey || cfg.orangeMoney?.merchantCode || cfg.orangeMoney?.apiKey || '',
        }
      };

      await fetchJson('/api/global-settings/integrations', {
        method: 'PUT',
        body: JSON.stringify(payload)
      });
      show(t('integrations.saved', 'Integration settings saved'));
    } catch (e) {
      show(e?.message || t('integrations.failedSave', 'Failed to save'), 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleTestEmail = async () => {
    setTestingEmail(true);
    try {
      const res = await fetchJson('/api/global-settings/integrations/test-email', { method: 'POST' });
      show(res?.message || 'Test email sent');
    } catch (e) {
      show(e?.message || 'Failed to send test email', 'error');
    } finally {
      setTestingEmail(false);
    }
  };

  const handleTestSms = async () => {
    setTestingSms(true);
    try {
      const res = await fetchJson('/api/global-settings/integrations/test-sms', { method: 'POST' });
      show(res?.message || 'Test SMS sent');
    } catch (e) {
      show(e?.message || 'Failed to send test SMS', 'error');
    } finally {
      setTestingSms(false);
    }
  };

  const setEmail = (key, val) => setCfg(prev => ({ ...prev, email: { ...prev.email, [key]: val } }));
  const setSms = (key, val) => setCfg(prev => ({ ...prev, sms: { ...prev.sms, [key]: val } }));
  const setMtn = (key, val) => setCfg(prev => ({ ...prev, mtnMomo: { ...prev.mtnMomo, [key]: val } }));
  const setOrange = (key, val) => setCfg(prev => ({ ...prev, orangeMoney: { ...prev.orangeMoney, [key]: val } }));
  const setStripe = (key, val) => setCfg(prev => ({ ...prev, stripe: { ...prev.stripe, [key]: val } }));

  const emailConfigured = !!(cfg.email?.smtpServer && cfg.email?.username);
  const smsConfigured = !!(cfg.sms?.provider && cfg.sms?.apiKey && cfg.sms?.enabled);
  const mtnConfigured = !!(
    (cfg.mtnMomo?.baseURL || '').trim() &&
    (cfg.mtnMomo?.subscriptionKey || '').trim() &&
    ((cfg.mtnMomo?.apiUser || cfg.mtnMomo?.apiUserId || '').trim()) &&
    (cfg.mtnMomo?.apiKey || '').trim() &&
    cfg.mtnMomo?.enabled
  );
  const orangeConfigured = !!(
    (cfg.orangeMoney?.merchantKey || '').trim() &&
    (cfg.orangeMoney?.authHeader || '').trim() &&
    (cfg.orangeMoney?.returnUrl || '').trim() &&
    (cfg.orangeMoney?.cancelUrl || '').trim() &&
    (cfg.orangeMoney?.notifUrl || '').trim() &&
    cfg.orangeMoney?.enabled
  );
  const stripeConfigured = !!(cfg.stripe?.publishableKey && cfg.stripe?.secretKey && cfg.stripe?.enabled);

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 10 }}><CircularProgress size={48} /></Box>;
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="h4">
          {t('integrations.title', 'Platform Service Configuration')}
        </Typography>
        <Button
          variant="contained"
          startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
          onClick={handleSave}
          disabled={saving || !canManage}
        >
          {saving ? t('integrations.saving', 'Saving...') : t('integrations.save', 'Save Settings')}
        </Button>
      </Box>

      <Alert severity="info" icon={<InfoIcon />} sx={{ mb: 3 }}>
        <strong>Platform-level services:</strong> These settings configure the default email, SMS, and payment
        services for the entire {platformName || 'AMECA'} platform. System emails (OTP, credentials, subscription invoices) use
        the email configured here. Schools can override email/SMS with their own settings.
      </Alert>

      {/* Status chips */}
      <Box sx={{ display: 'flex', gap: 1, mb: 3, flexWrap: 'wrap' }}>
        <Chip
          icon={emailConfigured ? <CheckCircleIcon /> : <EmailIcon />}
          label={emailConfigured ? 'Email Configured' : 'Email Not Configured'}
          color={emailConfigured ? 'success' : 'default'}
          variant={emailConfigured ? 'filled' : 'outlined'}
        />
        <Chip
          icon={smsConfigured ? <CheckCircleIcon /> : <SmsIcon />}
          label={smsConfigured ? 'SMS Enabled' : 'SMS Not Configured'}
          color={smsConfigured ? 'success' : 'default'}
          variant={smsConfigured ? 'filled' : 'outlined'}
        />
        <Chip
          icon={mtnConfigured ? <CheckCircleIcon /> : <PaymentIcon />}
          label={mtnConfigured ? 'MTN MoMo Active' : 'MTN MoMo Not Configured'}
          color={mtnConfigured ? 'success' : 'default'}
          variant={mtnConfigured ? 'filled' : 'outlined'}
        />
        <Chip
          icon={orangeConfigured ? <CheckCircleIcon /> : <PaymentIcon />}
          label={orangeConfigured ? 'Orange Money Active' : 'Orange Money Not Configured'}
          color={orangeConfigured ? 'success' : 'default'}
          variant={orangeConfigured ? 'filled' : 'outlined'}
        />
        <Chip
          icon={stripeConfigured ? <CheckCircleIcon /> : <PaymentIcon />}
          label={stripeConfigured ? 'Stripe Active' : 'Stripe Not Configured'}
          color={stripeConfigured ? 'success' : 'default'}
          variant={stripeConfigured ? 'filled' : 'outlined'}
        />
      </Box>

      {/* Email Configuration */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' }, gap: 1, mb: 2 }}>
          <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center' }}>
            <EmailIcon sx={{ mr: 1 }} /> Email Configuration (SMTP)
          </Typography>
          <Button
            variant="outlined" size="small"
            startIcon={testingEmail ? <CircularProgress size={16} /> : <EmailIcon />}
            onClick={handleTestEmail}
            disabled={testingEmail || !emailConfigured || !canManage}
          >
            {testingEmail ? 'Sending...' : 'Send Test Email'}
          </Button>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Configure the platform's default SMTP email server. All system emails (OTP, credentials, subscription notifications) use this configuration.
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="SMTP Server" value={cfg.email?.smtpServer || ''}
              onChange={e => setEmail('smtpServer', e.target.value)}
              placeholder="smtp.gmail.com" helperText="e.g. smtp.gmail.com, smtp.outlook.com" />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="SMTP Port" value={cfg.email?.smtpPort || ''}
              onChange={e => setEmail('smtpPort', e.target.value)}
              placeholder="587" helperText="Usually 587 (TLS) or 465 (SSL)" />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="Sender Name (From Name)" value={cfg.email?.fromName || platformName || 'AMECA'}
              onChange={e => setEmail('fromName', e.target.value)}
              placeholder={platformName || 'AMECA'} helperText="Display name shown to recipients" />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="Email Address / Username" value={cfg.email?.username || ''}
              onChange={e => setEmail('username', e.target.value)}
              placeholder={`noreply@${(platformName || 'ameca').toLowerCase()}-platform.com`} helperText="Sender address for all platform emails" />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="Password / App Password" type="password" value={cfg.email?.password || ''}
              onChange={e => setEmail('password', e.target.value)}
              helperText="For Gmail, use an App Password" />
          </Grid>
          <Grid item xs={12}>
            <FormControlLabel
              control={<Switch checked={cfg.email?.useSSL || false} onChange={e => setEmail('useSSL', e.target.checked)} />}
              label="Use SSL/TLS"
            />
            <FormControlLabel
              control={<Switch checked={cfg.email?.requireAuth !== false} onChange={e => setEmail('requireAuth', e.target.checked)} />}
              label="Require Authentication"
            />
          </Grid>
        </Grid>
      </Paper>

      {/* SMS Configuration */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' }, gap: 1, mb: 2 }}>
          <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center' }}>
            <SmsIcon sx={{ mr: 1 }} /> SMS Configuration
          </Typography>
          <Button
            variant="outlined" size="small"
            startIcon={testingSms ? <CircularProgress size={16} /> : <SmsIcon />}
            onClick={handleTestSms}
            disabled={testingSms || !smsConfigured || !canManage}
          >
            {testingSms ? 'Sending...' : 'Send Test SMS'}
          </Button>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Configure the platform's SMS provider for system-level text messages and notifications.
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>SMS Provider</InputLabel>
              <Select value={cfg.sms?.provider || ''} onChange={e => setSms('provider', e.target.value)} label="SMS Provider">
                <MenuItem value="">— Select Provider —</MenuItem>
                <MenuItem value="twilio">Twilio</MenuItem>
                <MenuItem value="africastalking">Africa's Talking</MenuItem>
                <MenuItem value="nexmo">Vonage (Nexmo)</MenuItem>
                <MenuItem value="custom">Custom SMS Gateway</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="API Key" value={cfg.sms?.apiKey || ''}
              onChange={e => setSms('apiKey', e.target.value)} placeholder="Your API Key" />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="API Secret" type="password" value={cfg.sms?.apiSecret || ''}
              onChange={e => setSms('apiSecret', e.target.value)} placeholder="Your API Secret" />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="Sender ID" value={cfg.sms?.senderId || ''}
              onChange={e => setSms('senderId', e.target.value)} placeholder={platformName || 'AMECA'}
              helperText="Appears as the SMS sender name" />
          </Grid>
          {cfg.sms?.provider === 'custom' && (
            <Grid item xs={12}>
              <TextField fullWidth label="Gateway URL" value={cfg.sms?.gatewayUrl || ''}
                onChange={e => setSms('gatewayUrl', e.target.value)}
                placeholder="https://api.smsprovider.com/send"
                helperText="Full URL of the custom SMS gateway endpoint" />
            </Grid>
          )}
          <Grid item xs={12}>
            <FormControlLabel
              control={<Switch checked={cfg.sms?.enabled || false} onChange={e => setSms('enabled', e.target.checked)} />}
              label="Enable SMS Notifications"
            />
          </Grid>
        </Grid>
      </Paper>

      {/* Payment Services Header */}
      <Typography variant="h5" sx={{ display: 'flex', alignItems: 'center', mb: 1, mt: 1 }}>
        <PaymentIcon sx={{ mr: 1 }} /> Payment Services
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Configure the payment gateways used for subscription billing. All three methods can be enabled simultaneously so users can choose their preferred payment method.
      </Typography>

      {/* MTN Mobile Money */}
      <Paper sx={{ p: 3, mb: 3, border: mtnConfigured ? '2px solid #ffc107' : undefined }}>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' }, gap: 1, mb: 2 }}>
          <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center' }}>
            <Box component="span" sx={{ bgcolor: '#ffc107', color: '#000', fontWeight: 700, borderRadius: 1, px: 1, py: 0.3, mr: 1, fontSize: 13 }}>MTN</Box>
            MTN Mobile Money (MoMo)
          </Typography>
          <Chip
            label={mtnConfigured ? 'Active' : 'Inactive'}
            color={mtnConfigured ? 'success' : 'default'}
            size="small"
          />
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Accept payments via MTN Mobile Money. Requires an MTN MoMo API subscription and credentials from the MTN Developer Portal.
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="API User ID" value={cfg.mtnMomo?.apiUserId || ''}
              onChange={e => setMtn('apiUserId', e.target.value)}
              placeholder="UUID from MTN Developer Portal"
              helperText="Your API User ID (UUID format)" />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="API Key" value={cfg.mtnMomo?.apiKey || ''}
              onChange={e => setMtn('apiKey', e.target.value)}
              placeholder="Your MTN MoMo API Key" />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="API Secret" type="password" value={cfg.mtnMomo?.apiSecret || ''}
              onChange={e => setMtn('apiSecret', e.target.value)}
              placeholder="Your API Secret" />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="Subscription Key" value={cfg.mtnMomo?.subscriptionKey || ''}
              onChange={e => setMtn('subscriptionKey', e.target.value)}
              placeholder="Primary or Secondary Key"
              helperText="From your MTN MoMo API subscription" />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="Collection Phone Number" value={cfg.mtnMomo?.phone || ''}
              onChange={e => setMtn('phone', e.target.value)}
              placeholder="+237XXXXXXXXX"
              helperText="MTN merchant phone number" />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="Callback / Webhook URL" value={cfg.mtnMomo?.webhookUrl || ''}
              onChange={e => setMtn('webhookUrl', e.target.value)}
              placeholder="https://your-domain.com/api/payments/mtn-callback"
              helperText="URL for payment status notifications" />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Environment</InputLabel>
              <Select value={cfg.mtnMomo?.environment || 'sandbox'} onChange={e => setMtn('environment', e.target.value)} label="Environment">
                <MenuItem value="sandbox">Sandbox (Testing)</MenuItem>
                <MenuItem value="production">Production (Live)</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="Currency" value={cfg.mtnMomo?.currency || 'XAF'}
              onChange={e => setMtn('currency', e.target.value)}
              helperText="e.g. XAF, EUR, USD" />
          </Grid>
          <Grid item xs={12}>
            <FormControlLabel
              control={<Switch checked={cfg.mtnMomo?.enabled || false} onChange={e => setMtn('enabled', e.target.checked)} color="warning" />}
              label="Enable MTN MoMo Payments"
            />
          </Grid>
        </Grid>
      </Paper>

      {/* Orange Money */}
      <Paper sx={{ p: 3, mb: 3, border: orangeConfigured ? '2px solid #ff6600' : undefined }}>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' }, gap: 1, mb: 2 }}>
          <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center' }}>
            <Box component="span" sx={{ bgcolor: '#ff6600', color: '#fff', fontWeight: 700, borderRadius: 1, px: 1, py: 0.3, mr: 1, fontSize: 13 }}>OM</Box>
            Orange Money
          </Typography>
          <Chip
            label={orangeConfigured ? 'Active' : 'Inactive'}
            color={orangeConfigured ? 'success' : 'default'}
            size="small"
          />
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Accept payments via Orange Money. Requires credentials from the Orange Developer portal or Orange Money merchant account.
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="Merchant Key" value={cfg.orangeMoney?.merchantKey || ''}
              onChange={e => setOrange('merchantKey', e.target.value)}
              placeholder="OM merchant_key" />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="Auth Header (Basic ...)" type="password" value={cfg.orangeMoney?.authHeader || ''}
              onChange={e => setOrange('authHeader', e.target.value)}
              placeholder="Basic base64(username:password)" />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="Return URL" value={cfg.orangeMoney?.returnUrl || ''}
              onChange={e => setOrange('returnUrl', e.target.value)}
              placeholder="https://your-domain.com/api/finance/payments/orange/return" />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="Cancel URL" value={cfg.orangeMoney?.cancelUrl || ''}
              onChange={e => setOrange('cancelUrl', e.target.value)}
              placeholder="https://your-domain.com/api/finance/payments/orange/cancel" />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="Notification URL" value={cfg.orangeMoney?.notifUrl || ''}
              onChange={e => setOrange('notifUrl', e.target.value)}
              placeholder="https://your-domain.com/api/finance/payments/webhooks/orange" />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="Country" value={cfg.orangeMoney?.country || 'cm'}
              onChange={e => setOrange('country', e.target.value)}
              helperText="Use cm for Cameroon" />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="Base URL" value={cfg.orangeMoney?.baseURL || 'https://api.orange.com'}
              onChange={e => setOrange('baseURL', e.target.value)}
              helperText="Usually https://api.orange.com" />
          </Grid>
          <Grid item xs={12}>
            <FormControlLabel
              control={<Switch checked={cfg.orangeMoney?.enabled || false} onChange={e => setOrange('enabled', e.target.checked)} sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#ff6600' }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: '#ff6600' } }} />}
              label="Enable Orange Money Payments"
            />
          </Grid>
        </Grid>
      </Paper>

      {/* Stripe */}
      <Paper sx={{ p: 3, mb: 3, border: stripeConfigured ? '2px solid #635bff' : undefined }}>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' }, gap: 1, mb: 2 }}>
          <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center' }}>
            <Box component="span" sx={{ bgcolor: '#635bff', color: '#fff', fontWeight: 700, borderRadius: 1, px: 1, py: 0.3, mr: 1, fontSize: 13 }}>S</Box>
            Stripe
          </Typography>
          <Chip
            label={stripeConfigured ? 'Active' : 'Inactive'}
            color={stripeConfigured ? 'success' : 'default'}
            size="small"
          />
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Accept card payments and international payments via Stripe. Requires a Stripe account with API keys from the Stripe Dashboard.
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="Publishable Key" value={cfg.stripe?.publishableKey || ''}
              onChange={e => setStripe('publishableKey', e.target.value)}
              placeholder="pk_live_... or pk_test_..."
              helperText="Starts with pk_live_ or pk_test_" />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="Secret Key" type="password" value={cfg.stripe?.secretKey || ''}
              onChange={e => setStripe('secretKey', e.target.value)}
              placeholder="sk_live_... or sk_test_..."
              helperText="Starts with sk_live_ or sk_test_" />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="Webhook Secret" type="password" value={cfg.stripe?.webhookSecret || ''}
              onChange={e => setStripe('webhookSecret', e.target.value)}
              placeholder="whsec_..."
              helperText="From Stripe Dashboard → Webhooks" />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="Webhook URL" value={cfg.stripe?.webhookUrl || ''}
              onChange={e => setStripe('webhookUrl', e.target.value)}
              placeholder="https://your-domain.com/api/payments/stripe-webhook"
              helperText="URL for Stripe webhook events" />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="Currency" value={cfg.stripe?.currency || 'XAF'}
              onChange={e => setStripe('currency', e.target.value)}
              helperText="e.g. XAF, EUR, USD, GBP" />
          </Grid>
          <Grid item xs={12}>
            <FormControlLabel
              control={<Switch checked={cfg.stripe?.enabled || false} onChange={e => setStripe('enabled', e.target.checked)} sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#635bff' }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: '#635bff' } }} />}
              label="Enable Stripe Payments"
            />
            <FormControlLabel
              control={<Switch checked={cfg.stripe?.testMode || false} onChange={e => setStripe('testMode', e.target.checked)} />}
              label="Test / Sandbox Mode"
            />
          </Grid>
        </Grid>
      </Paper>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={() => setSnackbar(s => ({ ...s, open: false }))} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
